'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems = [
    { label: 'Home', path: '/homepage' },
    { label: 'Programs', path: '/programs-overview' },
    { label: 'About', path: '/about' },
    { label: 'Success Stories', path: '/success-stories' },
    { label: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActivePath = (path: string) => pathname === path;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header
      className={`w-full bg-card shadow-md fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-modal' : 'shadow-card'
      } ${className}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link
            href="/homepage"
            className="flex items-center space-x-3 transition-transform duration-300 hover:scale-105"
            onClick={closeMobileMenu}
          >
            <div className="relative w-12 h-12 lg:w-16 lg:h-16">
              <Image
                src="/assets/images/IMG_4331_copy-Photoroom-1770448436115.png"
                alt="GO Speed Academy Logo"
                fill
                className="object-contain"
                priority
              />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                className={`px-4 py-2 text-sm font-heading font-semibold rounded-md transition-all duration-300 ${
                  isActivePath(item.path)
                    ? 'text-theme-orange bg-theme-orange/10' :'text-text-primary hover:text-theme-orange hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="https://forms.gle/9yvnUPvV2ifJin4S9"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-theme-orange text-white font-heading font-bold text-sm rounded-md shadow-card hover:shadow-hover hover:bg-theme-orange/90 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Book Trial Session
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-primary hover:bg-muted transition-colors duration-300"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card">
          <nav className="px-4 py-6 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                onClick={closeMobileMenu}
                className={`block px-4 py-3 text-base font-heading font-semibold rounded-md transition-all duration-300 ${
                  isActivePath(item.path)
                    ? 'text-theme-orange bg-theme-orange/10' :'text-text-primary hover:text-theme-orange hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="https://forms.gle/9yvnUPvV2ifJin4S9"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobileMenu}
              className="block w-full px-4 py-3 mt-4 bg-theme-orange text-white font-heading font-bold text-base text-center rounded-md shadow-card hover:shadow-hover hover:bg-theme-orange/90 transition-all duration-300"
            >
              Book Trial Session
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;